import React from "react"

import { Button, Group, NumberInput, Select, Slider, Stack, Switch, Text } from "@mantine/core"
import { IconMicrophone } from "@tabler/icons-react"

import ModuleSettingsCard from "../module-settings-card/ModuleSettingsCard"
import useAudioModuleSettings from "./useAudioModuleSettings"
import { ModuleSettingsProps } from "../module-settings-card/ModuleSettingsProps"
import { ModuleFC } from "../module-settings-card/ModuleFC"
import _DeviceAudioModuleSettings from "./_DeviceAudioModuleSettings"
import _GroupAudioModuleSettings from "./_GroupAudioModuleSettings"
import SettingsOverwriteWarning from "../settings-overwrite-warning/SettingsOverwriteWarning"
import { useLanguage } from "../../../../contexts/language-context"
import schedulePresets from "../operating-time-module-settings/schedulePresets"
import PresetSelectItem from "../operating-time-module-settings/PresetSelectItem"

type AudioModuleSettingsProps = ModuleSettingsProps<"duration" | "samplerate">

const AudioModuleSettings: ModuleFC<AudioModuleSettingsProps> = Object.assign(
    ({ attrs, organisation, group, device }) => {
        const { form, onSubmit, reset, inheritSettings, processing } = useAudioModuleSettings(
            attrs,
            organisation,
            group,
            device,
        )
        const { L } = useLanguage()

        return (
            <ModuleSettingsCard title={L("Audio settings")} icon={IconMicrophone}>
                <form onSubmit={form.onSubmit(onSubmit)}>
                    <Stack spacing="xl">
                        <SettingsOverwriteWarning attrs={attrs} group={group} device={device} />
                        <Stack spacing={0}>
                            <Text size="sm">{L("Enable Recording")}</Text>
                            <Switch
                                description={L("Whether the device should be recording.")}
                                label={L("Arm the recorder")}
                                checked={form.values.armed}
                                {...form.getInputProps("armed")}
                            />
                        </Stack>
                        <Stack spacing={0}>
                            <Text size="sm">{L("Recording duration (min.)")}</Text>
                            <NumberInput
                                description={L("The duration of the recorded sound files. Larger files take more time to process individually.")}
                                placeholder={L("Duration")}
                                min={1}
                                max={60}
                                {...form.getInputProps("duration")}
                                value={(form.values.duration as number | null) ?? ""}
                            />
                        </Stack>
                        <Stack spacing={0}>
                            <Text size="sm">{L("Recording gain (%)")}</Text>
                            <Text size="xs" color="dimmed">
                                {L("Determine the strength of the digital gain of the microphones.")}
                            </Text>
                            <Slider
                                mt="xs"
                                step={1}
                                min={1}
                                max={100}
                                label={(value: number | null) => (value ?? 0) + "%"}
                                disabled={!attrs?.gain.mutable}
                                {...form.getInputProps("gain")}
                            />
                        </Stack>
                        <Stack spacing="0">
                            <Text size="sm">{L("Audio sample rate (Hz.)")}</Text>
                            {/* list of common sample rates: https://github.com/audiojs/sample-rate */}
                            <Select
                                description={L("Sample rate should be twice the frequency of the recorded sound. File size increases with higher sample rates.")}
                                data={[
                                    "8000",
                                    "11025",
                                    "16000",
                                    "22050",
                                    "44100",
                                    "48000",
                                    "88200",
                                    "96000",
                                    "175400",
                                    "192000",
                                ]}
                                placeholder="Sample rate"
                                clearable
                                withinPortal
                                {...form.getInputProps("samplerate")}
                            />
                        </Stack>
                        <Group position="right">
                            {device && (
                                <Button
                                    variant="subtle"
                                    onClick={inheritSettings}
                                    disabled={Object.values(form.values).every(value => value === null)}
                                >
                                    {L("Apply group settings")}
                                </Button>
                            )}
                            <Button variant="subtle" onClick={reset} disabled={!form.isDirty()}>
                                {L("Reset")}
                            </Button>
                            <Button type="submit" disabled={!form.isDirty()} loading={processing}>
                                {L("Save")}
                            </Button>
                        </Group>
                    </Stack>
                </form>
            </ModuleSettingsCard>
        )
    },
    {
        Device: _DeviceAudioModuleSettings,
        Group: _GroupAudioModuleSettings
    }
)

export default AudioModuleSettings
